/**
 * TreatmentReadyPanel — M16B-07B
 *
 * Single canonical frontend consumer of the M16B-07A
 * Treatment Ready API. Every UI surface (MyOffers,
 * OfferComparison, Revision History, Lead Management,
 * Doctor Workspace, Coordinator Lens) renders ONLY through
 * this component.
 *
 * Modes:
 *   patient → GET /api/patient/leads/{leadId}/treatment-ready
 *             (redacted payload — no attestation IDs / rev numbers)
 *   clinic  → GET /api/clinic/leads/{leadId}/treatment-ready
 *             (full evidence for staff)
 *
 * STRICT RULES (drift-guarded):
 *   - The endpoint URL strings are literal here and ONLY here.
 *   - No client-side readiness computation.
 *   - Aggregation across slugs is forbidden — every slug is shown
 *     individually as `TreatmentReadyChip`.
 *   - When the API reports `no_active_offer`, we display the
 *     neutral "Offer in progress" state — never a red verdict.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import TreatmentReadyChip from './TreatmentReadyChip';

const ENDPOINTS = {
  patient: (leadId) => `/patient/leads/${leadId}/treatment-ready`,
  clinic:  (leadId) => `/clinic/leads/${leadId}/treatment-ready`,
};

// Read-only coordinator endpoint — fetches the most-recent N leads for
// a patient at this clinic and returns per-lead readiness. Used by the
// CoordinatorAttestationLens for read-only visibility (no override).
const COORDINATOR_PATIENT_ENDPOINT = (patientId, limit = 3) =>
  `/clinic/patients/${patientId}/treatment-ready-current?limit=${limit}`;

export default function TreatmentReadyPanel({
  leadId,
  patientId,           // coordinator path
  mode = 'patient',
  title,
  className = '',
}) {
  const { t } = useTranslation();
  const resolvedTitle = title || t('patient.treatmentReady.title');
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, error: null });

    let url;
    if (mode === 'coordinator' && patientId) {
      url = COORDINATOR_PATIENT_ENDPOINT(patientId, 3);
    } else if (leadId) {
      const builder = ENDPOINTS[mode] || ENDPOINTS.patient;
      url = builder(leadId);
    } else {
      setState({ loading: false, data: null, error: null });
      return undefined;
    }

    api.get(url)
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, data: res?.data || null, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          data: null,
          error: err?.response?.data?.message || t('patient.common.error'),
        });
      });
    return () => { cancelled = true; };
  }, [leadId, patientId, mode]);

  if (!leadId && !(mode === 'coordinator' && patientId)) return null;

  if (state.loading) {
    return (
      <div className={`text-xs text-slate-500 ${className}`} data-testid="treatment-ready-loading">
        {t('patient.common.loading')}
      </div>
    );
  }
  if (state.error) {
    return (
      <div className={`text-xs text-rose-600 ${className}`} data-testid="treatment-ready-error">
        {state.error}
      </div>
    );
  }
  if (!state.data) return null;

  // Coordinator mode: data shape is { leads: [{lead_id, treatment_interest, treatments[...], no_active_offer}] }
  if (mode === 'coordinator') {
    const leads = state.data.leads || [];
    return (
      <section
        className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}
        data-testid="treatment-ready-panel-coordinator"
      >
        <header className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-800">{resolvedTitle}</h4>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">
            {t('patient.treatmentReady.planDetails')}
          </span>
        </header>
        {leads.length === 0 && (
          <div className="text-xs text-slate-500" data-testid="treatment-ready-coordinator-empty">
            {t('patient.treatmentReady.empty')}
          </div>
        )}
        <div className="space-y-3" data-testid="treatment-ready-coordinator-list">
          {leads.map((ld) => (
            <div key={ld.lead_id} className="border border-slate-100 rounded-lg p-2">
              <div className="text-[11px] text-slate-500 mb-2">
                Lead <span className="font-mono">#{String(ld.lead_id).slice(-6)}</span>
                {ld.treatment_interest ? <> · {ld.treatment_display_name || ld.treatment_interest}</> : null}
              </div>
              {ld.no_active_offer ? (
                <div className="text-xs text-slate-500">{t('patient.treatmentReady.empty')}</div>
              ) : (ld.treatments || []).length === 0 ? (
                <div className="text-xs text-slate-500">{t('patient.treatmentReady.empty')}</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(ld.treatments || []).map((t) => (
                    <TreatmentReadyChip key={`${ld.lead_id}-${t.treatment_slug}`} verdict={t} mode="clinic" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  }

  const treatments = state.data.treatments || [];
  const noOffer = state.data.no_active_offer === true;

  return (
    <section
      className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}
      data-testid={`treatment-ready-panel-${mode}`}
    >
      <header className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-800">{resolvedTitle}</h4>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          {t('patient.treatmentReady.planDetails')}
        </span>
      </header>
      {noOffer && (
        <div className="text-xs text-slate-500" data-testid="treatment-ready-no-offer">
          {t('patient.treatmentReady.empty')}
        </div>
      )}
      {!noOffer && treatments.length === 0 && (
        <div className="text-xs text-slate-500" data-testid="treatment-ready-empty">
          {t('patient.treatmentReady.empty')}
        </div>
      )}
      <div className="flex flex-wrap gap-2" data-testid="treatment-ready-list">
        {treatments.map((t) => (
          <TreatmentReadyChip key={t.treatment_slug} verdict={t} mode={mode} />
        ))}
      </div>
    </section>
  );
}

export { ENDPOINTS };
